/**
 * Prompt translations service
 * Loads and provides translations for AI prompts
 */
import { type AvailableLanguage, getCurrentLanguage } from './i18n';

// Cache for loaded prompt translations
const promptTranslationCache = new Map<
    AvailableLanguage,
    Record<string, PromptTranslation>
>();

export interface PromptTranslation {
    seoTitle: string;
    seoDescription: string;
    title: string;
    example: string;
    description: string;
    textLabel: string;
    textPlaceholder: string;
}

/**
 * Load prompt translations for a specific language
 * Works on both server (Node.js) and client (browser)
 */
export async function loadPromptTranslations(
    language: AvailableLanguage,
): Promise<Record<string, PromptTranslation>> {
    // Check cache first
    if (promptTranslationCache.has(language)) {
        return promptTranslationCache.get(language)!;
    }

    try {
        let translations: Record<string, PromptTranslation> = {};

        if (typeof window === 'undefined') {
            // Server-side: Use Node.js fs to read from app/i18n/prompts folder
            const fs = await import('fs');
            const path = await import('path');
            const filePath = path.join(
                process.cwd(),
                'app',
                'i18n',
                'prompts',
                `${language}.prompts.json`,
            );

            if (fs.existsSync(filePath)) {
                const content = fs.readFileSync(filePath, 'utf-8');
                translations = JSON.parse(content);
            }
        } else {
            // Client-side: Dynamic import of JSON files
            try {
                switch (language) {
                    case 'en':
                        translations = (
                            await import('~/i18n/prompts/en.prompts.json')
                        ).default;
                        break;
                    case 'ru':
                        translations = (
                            await import('~/i18n/prompts/ru.prompts.json')
                        ).default;
                        break;
                    case 'fr':
                        translations = (
                            await import('~/i18n/prompts/fr.prompts.json')
                        ).default;
                        break;
                    case 'de':
                        translations = (
                            await import('~/i18n/prompts/de.prompts.json')
                        ).default;
                        break;
                    case 'it':
                        translations = (
                            await import('~/i18n/prompts/it.prompts.json')
                        ).default;
                        break;
                    case 'es':
                        translations = (
                            await import('~/i18n/prompts/es.prompts.json')
                        ).default;
                        break;
                    case 'id':
                        translations = (
                            await import('~/i18n/prompts/id.prompts.json')
                        ).default;
                        break;
                    case 'ar':
                        translations = (
                            await import('~/i18n/prompts/ar.prompts.json')
                        ).default;
                        break;
                    case 'th':
                        translations = (
                            await import('~/i18n/prompts/th.prompts.json')
                        ).default;
                        break;
                    case 'vi':
                        translations = (
                            await import('~/i18n/prompts/vi.prompts.json')
                        ).default;
                        break;
                    case 'tr':
                        translations = (
                            await import('~/i18n/prompts/tr.prompts.json')
                        ).default;
                        break;
                    case 'nl':
                        translations = (
                            await import('~/i18n/prompts/nl.prompts.json')
                        ).default;
                        break;
                    case 'pl':
                        translations = (
                            await import('~/i18n/prompts/pl.prompts.json')
                        ).default;
                        break;
                    case 'ko':
                        translations = (
                            await import('~/i18n/prompts/ko.prompts.json')
                        ).default;
                        break;
                    case 'hi':
                        translations = (
                            await import('~/i18n/prompts/hi.prompts.json')
                        ).default;
                        break;
                    default:
                        // Default to English if language not found
                        translations = (
                            await import('~/i18n/prompts/en.prompts.json')
                        ).default;
                }
            } catch (importError) {
                // Import failed, try to load English as fallback
                console.warn(
                    `Failed to import prompt translations for ${language}:`,
                    importError,
                );
                try {
                    translations = (
                        await import('~/i18n/prompts/en.prompts.json')
                    ).default;
                } catch {
                    translations = {};
                }
            }
        }

        // Cache the result
        promptTranslationCache.set(language, translations);
        return translations;
    } catch (error) {
        console.error('Error loading prompt translations:', error);
        // Return empty object if loading fails
        const emptyTranslations = {};
        promptTranslationCache.set(language, emptyTranslations);
        return emptyTranslations;
    }
}

/**
 * Get a translated prompt by ID
 * @param promptId The ID of the prompt to translate
 * @param originalPrompt The original prompt object (used as fallback)
 * @returns The translated prompt or the original if translation not found
 */
export async function getTranslatedPrompt(
    promptId: string,
    originalPrompt: any,
): Promise<any> {
    const language = getCurrentLanguage();

    // For English, return the original prompt
    if (language === 'en') {
        return originalPrompt;
    }

    try {
        const translations = await loadPromptTranslations(language);
        const translation = translations[promptId];

        if (translation) {
            // Merge the translation with the original prompt
            // Keep non-translatable fields from the original
            return {
                ...originalPrompt,
                seoTitle: translation.seoTitle || originalPrompt.seoTitle,
                seoDescription:
                    translation.seoDescription || originalPrompt.seoDescription,
                title: translation.title || originalPrompt.title,
                example: translation.example || originalPrompt.example,
                description: translation.description || originalPrompt.description,
                textLabel: translation.textLabel || originalPrompt.textLabel,
                textPlaceholder:
                    translation.textPlaceholder || originalPrompt.textPlaceholder,
            };
        }
    } catch (error) {
        console.error('Error getting translated prompt:', error);
    }

    // Return original prompt if translation fails
    return originalPrompt;
}

/**
 * Get all translated prompts
 * @param originalPrompts Array of original prompts
 * @returns Array of translated prompts
 */
export async function getTranslatedPrompts(
    originalPrompts: any[],
): Promise<any[]> {
    const language = getCurrentLanguage();

    // For English, return the original prompts
    if (language === 'en') {
        return originalPrompts;
    }

    try {
        const translations = await loadPromptTranslations(language);

        return originalPrompts.map((prompt) => {
            const translation = translations[prompt.id];

            if (translation) {
                // Merge the translation with the original prompt
                return {
                    ...prompt,
                    seoTitle: translation.seoTitle || prompt.seoTitle,
                    seoDescription:
                        translation.seoDescription || prompt.seoDescription,
                    title: translation.title || prompt.title,
                    example: translation.example || prompt.example,
                    description: translation.description || prompt.description,
                    textLabel: translation.textLabel || prompt.textLabel,
                    textPlaceholder:
                        translation.textPlaceholder || prompt.textPlaceholder,
                };
            }

            return prompt;
        });
    } catch (error) {
        console.error('Error getting translated prompts:', error);
        return originalPrompts;
    }
}

/**
 * Clear prompt translation cache (useful for development/testing)
 */
export function clearPromptTranslationCache(): void {
    promptTranslationCache.clear();
}